import React, { useRef, useState } from 'react';
import Modal from 'shared/modals/Modal';
import { addLegacyDetails, ignoreOrganizationField, updateOrganizationField } from '../../../../fetch/organizations';
import BaseButton from '../../../../shared/components/buttons';
import { BaseCell } from '../../../../shared/components/grids/Cells';
import { useToast } from 'shared/toast';
import DataGrid from '../../../../shared/components/grids/DataGrid/DataGrid';

import './style.scss';
import { BaseCheckbox } from '../../../../shared/components/inputs';
import OrganizationPrimarySelection
from 'src/organizations/components/modals/OrganizationConflictsModal/OrganizationPrimarySelection';

const OrganizationConflictsModal = ({
  selectedv1Organizations, selectedv2Organization, onClose, getData,
}) => {
  const toast = useToast();

  const manualMappingFields = ['Address', 'Location', 'Contact', 'Website', 'Sage Tag'];

  const [noConflicts, setNoConflicts] = useState(false);
  const [gridNeedsReload, setGridNeedsReload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updateRoles, setUpdateRoles] = useState(false);
  const v1Roles = selectedv1Organizations.map((s) => s.type.toString().toLowerCase());
  const v2Roles = selectedv2Organization.roles.map((r) => r.toString().toLowerCase());
  const [missingV2Roles, setMissingV2Roles] = useState(v1Roles.filter((x) => !v2Roles.includes(x)).length > 0);
  const modalRef = useRef(null);

  const handleLink = () => {
    const values = modalRef.current?.getValues();

    const details = new Object();

    values.forEach(({ legacyId, ...others }) => {
      details[legacyId] = others;
    });

    setIsLoading(true);

    addLegacyDetails(details, selectedv2Organization.id, updateRoles)
      .then(() => {
        toast.success({ message: 'Saved Successfully' });
        if (updateRoles) {
          // Refresh conflicts. There might be new ones since we might have updated the roles
          setMissingV2Roles(false);
          setUpdateRoles(false);
          setNoConflicts(false);
          setGridNeedsReload(true);
        } else {
          onClose(true);
        }
      })
      .catch((error) => {
        toast.error({ message: 'Failed to save', error });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleUpdate = (dataItem) => {
    setIsLoading(true);
    updateOrganizationField({
      organizationLegacyId: dataItem.sourceEntityId,
      organizationId: selectedv2Organization.id,
      propertyName: dataItem.propertyName,
      value: dataItem.sourceValue,
    })
      .then(() => {
        toast.success({ message: 'Saved Successfully' });
        setGridNeedsReload(true);
      })
      .catch((error) => {
        toast.error({ message: 'Failed to save', error });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleIgnore = (dataItem) => {
    setIsLoading(true);
    ignoreOrganizationField({
      v1OrganizationId: dataItem.sourceEntityId,
      v2OrganizationId: selectedv2Organization.id,
      propertyName: dataItem.propertyName,
    })
      .then(() => {
        toast.success({ message: 'Saved Successfully' });
        setGridNeedsReload(true);
      })
      .catch((error) => {
        toast.error({ message: 'Failed to save', error });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleGridLoaded = (data) => {
    setNoConflicts(data.length === 0);
    setGridNeedsReload(false);
  };

  const handleAddManually = (dataItem) => {
    if (dataItem.propertyName === 'Contact') {
      window.open(`organizations/organizations/${selectedv2Organization.id}/contacts`, '_blank', 'noreferrer');
      return;
    }

    if (dataItem.propertyName === 'Location') {
      window.open(`organizations/organizations/${selectedv2Organization.id}/locations`, '_blank', 'noreferrer');
      return;
    }

    window.open(`organizations/organizations/${selectedv2Organization.id}`, '_blank', 'noreferrer');
  };

  return (
    <Modal
      isOpen
      containerStyle='large'
      title={
        !noConflicts
          ? 'Please resolve these conflicts on v1 before mapping'
          : 'There are no conflicts between the selected organizations. Would you like to link them now?'
      }
      handleClose={onClose}
      className='organizations-conflicts'
    >
      {!noConflicts && (
        <DataGrid
          getData={getData}
          hideActionsCell
          hasExport={false}
          hasUnifiedTextSearch={false}
          didLoad={handleGridLoaded}
          forceReload={gridNeedsReload}
          resizable
          actionBarContent={(
            <>
              <BaseButton icon='refresh' title='Refresh' onClick={() => setGridNeedsReload(true)} />
            </>
          )}
          columns={[
            {
              title: 'Recno',
              field: 'sourceEntityId',
              width: 100,
            },
            { title: 'Type', field: 'additionalInformation', width: 150 },
            {
              title: 'Property',
              field: 'propertyName',
              cell: ({ dataItem }) => (
                <BaseCell style={{ fontWeight: 'bold' }}>
                  {dataItem.propertyName.includes('.') ? dataItem.propertyName.split('.')[1] : dataItem.propertyName}
                </BaseCell>
              ),
            },
            {
              title: 'v1 Value',
              field: 'sourceValue',
            },
            {
              title: 'v2 Value',
              field: 'destinationValue',
              cell: ({ dataItem }) => (
                <BaseCell style={{ whiteSpace: 'pre' }}>
                 {dataItem.destinationValue}
                </BaseCell>
              ),
            },
            {
              title: 'Actions',
              cell: ({ dataItem }) => (
                <BaseCell>
                  <BaseButton
                    icon='cancel'
                    title='Ignore v1 Value'
                    onClick={() => handleIgnore(dataItem)}
                    disabled={isLoading}
                    isLoading={isLoading}
                  />
                  {!manualMappingFields.includes(dataItem.propertyName) && (
                    <BaseButton
                      primary
                      title='Update to v1 Value'
                      icon='check'
                      onClick={() => handleUpdate(dataItem)}
                      style={{ marginLeft: '1rem' }}
                      disabled={isLoading}
                      isLoading={isLoading}
                    />
                  )}
                  {manualMappingFields.includes(dataItem.propertyName) && (
                    <BaseButton
                      primary
                      title='Add Manually'
                      icon='add'
                      role='link'
                      onClick={() => handleAddManually(dataItem)}
                      style={{ marginLeft: '1rem' }}
                      disabled={isLoading}
                      isLoading={isLoading}
                    />
                  )}
                </BaseCell>
              ),
            },
          ]}
        />
      )}
      {noConflicts && (
        <>
          {missingV2Roles && (
            <>
              <BaseCheckbox
                label='Update v2 Organization Roles'
                value={updateRoles}
                onChange={({ value }) => setUpdateRoles(value)}
              />
              <br />
              <br />
            </>
          )}
          <OrganizationPrimarySelection
            ref={modalRef}
            loadExistingLegacyDetails
            v2Organization={selectedv2Organization}
            v1Organizations={selectedv1Organizations}
          />
          <BaseButton
            primary
            title={updateRoles ? 'Update Roles' : 'Link'}
            icon='checkmark'
            onClick={() => handleLink()}
            disabled={isLoading}
          />
        </>
      )}
    </Modal>
  );
};

export default OrganizationConflictsModal;
